import { Edge } from '@xyflow/react';

import { GoalType, IConditionFlow, IMission, IScene, SceneType } from '@/interface';

import { NodeEnums } from '../const/node';
import { ConditionType, IOption, ISpecifyMission, NextAction } from '../nodes/condition/const';
import { templateNode } from '../nodes/templateNode';
import { AppNode } from '../types';
import { conditionGroupNode, getEdges } from './exclSceneToNodes';
import { getNodeId } from './getNodeId';

export const refreshOptions = (nodes: AppNode[], rootNode: AppNode) => {
  if (rootNode?.data?.info?.sceneType === SceneType.SCENE_TYPE_OPTION) {
    const options: IOption[] = rootNode?.data?.info?.conditions?.map((cond: IConditionFlow) => {
      const m = cond.missions?.[0];
      if (m) {
        return {
          value: m.id,
          label: m.name || '未命名',
          disabled: false,
        };
      }
      return undefined;
    }).filter((e: IOption) => e !== undefined);
    return options;
  }

  const options: IOption[] = rootNode?.data?.info?.options || [];
  const cards = rootNode?.data?.shareInfo?.mainMission?.cards || [];
  const newOptions: IOption[] = [];

  cards.forEach((card) => {
    const id = card.id!;
    const name = card.name!;
    const countDown = isCountDown(card)
      ? { isCountDown: true, reason: '倒计时触发的剧情卡不支持设置过关条件' }
      : { isCountDown: false, reason: '' };
    let option = options.find(e => e.value === id);
    if (option) {
      option = { ...option, label: name || '未命名', ...countDown }; // 更新
    } else {
      // 新增
      option = {
        value: id,
        label: name || '未命名',
        disabled: false,
        ...countDown,
      };
    }

    newOptions.push(option);
  });

  const updatedOptions = newOptions.map((opt) => {
    const isSelected = nodes.find(node => node.data?.info?.rootId === rootNode.id
      && node.data?.info?.missions?.some((m: ISpecifyMission) => m.id === opt.value));

    return {
      ...opt,
      disabled: !!isSelected,
    };
  });

  return updatedOptions;
};

const isCountDown = (m: Partial<IMission>) => {
  const { goalType, countDown } = m.missionGoal || {};
  return goalType === GoalType.GOAL_TYPE_COUNTDOWN && countDown?.isOpen === true;
};

export const changeConditionNode = (
  nodes: AppNode[],
  options: IOption[],
  rootId: string,
) => nodes.map((node: AppNode) => {
  const info = node.data?.info;
  if (!info || info.rootId !== rootId) {
    return node;
  }

  const newInfo = { ...info };
  if (!newInfo.isEmpty && newInfo.type === ConditionType.PASS && !newInfo.missions) {
    newInfo.isDraft = true;
  }
  const newMissions = newInfo.missions?.map((m: ISpecifyMission) => {
    const newMission = { ...m };
    const option = options.find(opt => opt.value === m.id);
    if (option) {
      newMission.name = option.label;
      newMission.isError = false;
    } else {
      newInfo.isDraft = true;
      newMission.isError = true;
    }
    return newMission;
  });
  newInfo.missions = newMissions;

  return {
    ...node,
    data: {
      ...node.data,
      info: newInfo,
    },
  };
});

// 在getNodes中调用，数据流处理过程中，依据scene节点来处理条件节点
export const processCond = (initNodes: AppNode[], initEdges: Edge[]) => {
  let nodes = [...initNodes];
  let edges = [...initEdges];
  const scenesToProcess: { scene: IScene, isNew: boolean, nextAction: NextAction }[] = [];

  // 首先收集所有需要处理的scene节点
  nodes.forEach((node) => {
    const info = node.data?.info;
    if (node.type === NodeEnums.scene && info?.condUtils) {
      const { isNew, nextAction } = info.condUtils;
      scenesToProcess.push({ scene: info, isNew, nextAction });
    }
  });

  // 依次处理每个scene节点
  scenesToProcess.forEach(({ scene, isNew, nextAction }) => {
    const EMPTY = { newNodes: [], newEdges: [] };
    let result: { newNodes: AppNode[], newEdges: Edge[] } = EMPTY;

    switch (nextAction) {
      case NextAction.filterJump:
        result = filterJumpNode(scene, nodes);
        break;
      case NextAction.initCond:
        result = initCondNode(scene, nodes) || EMPTY;
        break;
      case NextAction.changeFixedCond:
        result = changeFixedCondNode(scene, nodes, isNew) || EMPTY;
        break;
      default:
        break;
    }

    // 更新全局的nodes和edges，保证每次操作都是基于最新的节点
    if (result.newNodes.length > 0 && result.newEdges.length > 0) {
      nodes = result.newNodes;
      edges = result.newEdges;
    }
  });

  // 删除所有节点的 condUtils 字段
  nodes = nodes.map((node) => {
    if (node.data?.info?.condUtils) {
      const newNode = {
        ...node,
        data: {
          ...node.data,
          info: { ...node.data.info },
        },
      };
      delete newNode.data.info.condUtils;
      return newNode;
    }
    return node;
  });

  return { nodes, edges };
};

// 针对已有幕，关闭isFinal后，需要初始化条件节点
export const initCondNode = (
  scene: IScene,
  nodes: AppNode[],
) => {
  const rootType = scene.sceneType === SceneType.SCENE_TYPE_OPTION
    ? NodeEnums.scene
    : NodeEnums.mainMission;
  const rootIndex = nodes.findIndex(node => node.type === rootType
    && node.data?.shareInfo?.scene?.id === scene.id);
  if (rootIndex === -1) return;

  // 直接添加条件节点的模板
  const rootNode = templateNode[NodeEnums.addCondition];
  const addNodes = conditionGroupNode(rootNode, rootIndex, scene);

  // 过滤旧的条件节点
  const types = new Set([
    NodeEnums.addCondition, NodeEnums.passCondition, NodeEnums.trapCondition,
    NodeEnums.successMission, NodeEnums.failMission, NodeEnums.jump,
  ]);
  if (scene.sceneType === SceneType.SCENE_TYPE_OPTION) {
    types.add(NodeEnums.collapse);
  }
  const newNodes = nodes.filter(node => !(types.has(node.type as NodeEnums)
    && node.data?.shareInfo?.scene?.id === scene.id));
  const nodesToAdd = [
    ...(scene.sceneType === SceneType.SCENE_TYPE_OPTION
      ? [{ ...templateNode[NodeEnums.collapse], id: getNodeId(0) }]
      : []),
    ...addNodes,
  ];

  newNodes.splice(rootIndex + 1, 0, ...nodesToAdd);
  const newEdges = getEdges(newNodes);
  return { newNodes, newEdges };
};

export const changeFixedCondNode = (
  scene: IScene,
  nodes: AppNode[],
  isNew: boolean,
) => {
  let rootIndex = nodes.findIndex(node => node.type === NodeEnums.addCondition
    && node.data?.shareInfo?.scene?.id === scene.id);
  if (rootIndex === -1) {
    if (!isNew) return; // 如果不是新建幕，找不到root则返回
    const preNodes: AppNode[] = [];
    const group = [templateNode[NodeEnums.collapse], templateNode[NodeEnums.addCondition]];
    for (let i = 0; i < group.length; i++) {
      const e = group[i];
      const id = getNodeId(i);
      preNodes.push({ ...e, id });
    }
    nodes.push(...preNodes);
    rootIndex = nodes.length - 1;
  }

  const groupNodes = conditionGroupNode(nodes[rootIndex], rootIndex, scene);
  const types = new Set([
    NodeEnums.addCondition, NodeEnums.passCondition, NodeEnums.trapCondition,
    NodeEnums.successMission, NodeEnums.failMission, NodeEnums.jump,
  ]);
  const newNodes = nodes.filter(node => !(types.has(node.type as NodeEnums)
    && node.data?.shareInfo?.scene?.id === scene.id));
  newNodes.splice(rootIndex, isNew ? 1 : 0, ...groupNodes);
  const newEdges = getEdges(newNodes);
  return { newNodes, newEdges };
};

// 针对已有幕，设置isFinal后，过滤掉所有条件节点，接一个jump
export const filterJumpNode = (
  scene: IScene,
  nodes: AppNode[],
) => {
  const types = new Set([
    NodeEnums.addCondition, NodeEnums.passCondition, NodeEnums.trapCondition,
    NodeEnums.successMission, NodeEnums.failMission, NodeEnums.jump,
  ]);
  if (scene.sceneType === SceneType.SCENE_TYPE_OPTION) {
    types.add(NodeEnums.collapse);
  }
  const newNodes = nodes.filter(node => !(types.has(node.type as NodeEnums)
    && node.data?.shareInfo?.scene?.id === scene.id));

  let insertIndex = newNodes.length;
  for (let i = 0; i < newNodes.length; i++) {
    if (newNodes[i].data?.shareInfo?.scene?.id === scene.id) {
      insertIndex = i + 1;
    } else if (insertIndex < newNodes.length) {
      break;
    }
  }
  const jumpNode = {
    ...templateNode[NodeEnums.jump],
    id: getNodeId(insertIndex),
    data: {
      ...templateNode[NodeEnums.jump].data,
      info: {},
    },
  };
  newNodes.splice(insertIndex, 0, jumpNode);
  const newEdges = getEdges(newNodes);
  return { newNodes, newEdges };
};

export const changeJumpNode = (nodes: AppNode[], condId: string, nextSceneId: string): AppNode[] | undefined => {
  let newCondition: IConditionFlow | undefined;
  let foundCondNode = false;
  let foundRootNode = false;
  let foundJumpNode = false;

  const updatedNodes = nodes.map((node) => {
    if (node.data?.info?.id === condId) {
      // 首先更新过关条件节点
      foundCondNode = true;
      const updatedNode = {
        ...node,
        data: {
          ...node.data,
          info: {
            ...node.data.info,
            nextSceneId,
          },
        },
      };
      newCondition = updatedNode.data.info;
      return updatedNode;
    }
    return node;
  });

  const newNodes = updatedNodes.map((node) => {
    if (newCondition && node.id === newCondition.rootId) {
      // 更新 addCondition 节点
      foundRootNode = true;
      const conditions = (node.data?.info?.conditions as IConditionFlow[])
        .map(e => (e.id === condId ? newCondition : e));
      return {
        ...node,
        data: {
          ...node.data,
          info: {
            ...node.data.info,
            conditions,
          },
        },
      };
    }

    if (newCondition && node.type === NodeEnums.jump && node.data?.conditionNodeId === newCondition.conditionNodeId) {
      // 更新跳转节点
      foundJumpNode = true;
      return {
        ...node,
        data: {
          ...node.data,
          info: {
            ...node.data?.info,
            nextSceneId,
          },
        },
      };
    }

    return node;
  });

  // 如果有一个节点未找到，则返回 undefined
  if (!foundCondNode || !foundRootNode || !foundJumpNode) return;

  return newNodes;
};

export const postCondition = (conditions: IConditionFlow[]) => {
  if (conditions.length === 0) {
    return [];
  }

  return conditions
    .filter(e => e.id !== '')
    .map(e => ({
      condId: e.id,
      triggerMissionId: e.triggerMissionId,
      nextSceneId: e.nextSceneId,
      passCondType: e.type,
      specifyMission: {
        missions: e.missions?.map(m => ({
          missionId: m.id,
          missionName: m.name,
        })),
      },
      triggerTrap: {
        countDown: e.openCount ? e.countDown : 0,
        specifyNumeric: {
          minVal: e.triggerValue?.minV,
          maxVal: e.triggerValue?.maxV,
          isUsed: e.openTrigger,
        },
      },
    }));
};
